import { useRef, useState } from 'react';

import { MessageCircle } from 'lucide-react';

import { useChatStore } from '@/stores/chat.store';

const POSITION_KEY = 'chat-bubble-position';
const BUBBLE_SIZE = 48; // h-12 w-12
const EDGE_MARGIN = 8;
const DRAG_THRESHOLD = 5; // px of movement before a press counts as a drag

interface BubblePosition {
  x: number;
  y: number;
}

const clampToViewport = (pos: BubblePosition): BubblePosition => ({
  x: Math.min(Math.max(pos.x, EDGE_MARGIN), window.innerWidth - BUBBLE_SIZE - EDGE_MARGIN),
  y: Math.min(Math.max(pos.y, EDGE_MARGIN), window.innerHeight - BUBBLE_SIZE - EDGE_MARGIN),
});

const getSavedPosition = (): BubblePosition | null => {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BubblePosition;
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return null;
    // Re-clamp on load in case the window shrank since the position was saved.
    return clampToViewport(parsed);
  } catch {
    return null;
  }
};

export const ChatBubble = () => {
  const { isOpen, togglePanel } = useChatStore();
  // null = default bottom-right corner (never dragged)
  const [position, setPosition] = useState<BubblePosition | null>(getSavedPosition);
  const dragStartRef = useRef<{
    pointerX: number;
    pointerY: number;
    bubble: BubblePosition;
  } | null>(null);
  const draggedRef = useRef(false);

  if (isOpen) return null;

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragStartRef.current = {
      pointerX: e.clientX,
      pointerY: e.clientY,
      bubble: { x: rect.left, y: rect.top },
    };
    draggedRef.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const start = dragStartRef.current;
    if (!start) return;
    const dx = e.clientX - start.pointerX;
    const dy = e.clientY - start.pointerY;
    if (!draggedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    draggedRef.current = true;
    setPosition(clampToViewport({ x: start.bubble.x + dx, y: start.bubble.y + dy }));
  };

  const handlePointerUp = () => {
    if (dragStartRef.current && draggedRef.current) {
      setPosition((pos) => {
        if (pos) localStorage.setItem(POSITION_KEY, JSON.stringify(pos));
        return pos;
      });
    }
    dragStartRef.current = null;
  };

  return (
    <button
      type="button"
      onClick={() => {
        // A drag release lands on the button — don't treat it as a click.
        if (draggedRef.current) {
          draggedRef.current = false;
          return;
        }
        togglePanel();
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={
        position ? { left: position.x, top: position.y, right: 'auto', bottom: 'auto' } : undefined
      }
      className="bg-primary text-primary-foreground fixed right-6 bottom-6 z-50 flex h-12 w-12 cursor-grab touch-none items-center justify-center rounded-full shadow-lg transition-transform select-none hover:scale-105 active:cursor-grabbing"
      title="Assistant — drag to move"
    >
      <MessageCircle className="h-5 w-5" />
    </button>
  );
};
